import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
    Badge,
    Flex,
    Switch,
    FormControl,
    FormLabel,
    Button,
    useToast,
    ModalFooter,
    Heading,
    Spinner,
    InputGroup,
    InputRightElement,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverFooter,
    PopoverArrow,
    PopoverCloseButton,
} from '@chakra-ui/react';
import { getOptionPositions, getTradeHistory, closePosition, updateStockStopLossPrice, reducePositionHalf } from '../api';
import { RepeatIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';

const OptionPositions = () => {
    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [tradeHistory, setTradeHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isStopLossOpen,
        onOpen: onStopLossOpen,
        onClose: onStopLossClose,
    } = useDisclosure();
    const [stopLossPrice, setStopLossPrice] = useState("");
    const [editingPosition, setEditingPosition] = useState(null);
    const toast = useToast();
    const [filters, setFilters] = useState({
        symbol: '',
        status: '',
        date: '',
        strategy: '',
    });
    const [positionToClose, setPositionToClose] = useState(null);
    const [positionToReduce, setPositionToReduce] = useState(null);
    const { isOpen: isClosePopoverOpen, onOpen: onClosePopoverOpen, onClose: onClosePopoverClose } = useDisclosure();
    const { isOpen: isReducePopoverOpen, onOpen: onReducePopoverOpen, onClose: onReducePopoverClose } = useDisclosure();

    const fetchPositions = async () => {
        try {
            // 获取当前时间（美东时间）
            const now = new Date();
            const etDate = filters.showAllDates ? '' : new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
            
            const data = await getOptionPositions({
                ...filters,
                date: etDate ? etDate.toISOString() : '',
            });
            console.log('Fetched positions:', data); // 添加调试日志
            setPositions(data);
        } catch (error) {
            console.error('获取期权持仓失败:', error);
        }
    };

    const fetchTradeHistory = async (positionId) => {
        try {
            const data = await getTradeHistory(positionId);
            setTradeHistory(data);
        } catch (error) {
            console.error('获取交易历史失败:', error);
        }
    };

    useEffect(() => {
        fetchPositions();
    }, [filters]);

    const handleRowClick = async (position) => {
        setSelectedPosition(position);
        await fetchTradeHistory(position.id);
        onOpen();
    };

    const getPositionTypeBadge = (type) => {
        const colors = {
            'CALL': 'green',
            'PUT': 'red',
        };
        const text = {
            'CALL': '看涨',
            'PUT': '看跌',
        };
        return (
            <Badge colorScheme={colors[type]}>
                {text[type]}
            </Badge>
        );
    };

    const getStatusBadge = (status) => {
        const colors = {
            'OPEN': 'green',
            'PARTIAL_CLOSED': 'yellow',
            'CLOSED': 'red',
        };
        const text = {
            'OPEN': '开仓',
            'PARTIAL_CLOSED': '部分平仓',
            'CLOSED': '平仓',
        };
        return (
            <Badge colorScheme={colors[status]}>
                {text[status]}
            </Badge>
        );
    };

    const getTradeTypeBadge = (type) => {
        const typeColors = {
            'ENTRY': 'green',
            'PARTIAL_EXIT': 'yellow',
            'FULL_EXIT': 'red',
        };
        const typeText = {
            'ENTRY': '开仓',
            'PARTIAL_EXIT': '部分平仓',
            'FULL_EXIT': '全部平仓',
        };
        return (
            <Badge colorScheme={typeColors[type] || 'gray'}>
                {typeText[type] || type}
            </Badge>
        );
    };

    // 计算盈亏百分比
    const calculatePnL = (position) => {
        if (!position.current_price || !position.entry_price) return null;
        const pnl = ((position.current_price - position.entry_price) / position.entry_price) * 100;
        return pnl.toFixed(2);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('zh-CN');
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            await fetchPositions();
            toast({
                title: "刷新成功",
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "刷新失败",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const calculateTotalPnL = () => {
        console.log('Positions:', positions); // 添加调试日志
        const total = positions.reduce((total, position) => {
            console.log('Position:', position.symbol, 'PnL:', position.total_profit_loss); // 添加调试日志
            const pnl = parseFloat(position.total_profit_loss) || 0;
            return total + pnl;
        }, 0);
        console.log('Total PnL:', total); // 添加调试日志
        return Number(total) || 0;
    };

    const handleClosePosition = async (position, e) => {
        if (e) e.stopPropagation();
        setPositionToClose(position);
        onClosePopoverOpen();
    };

    const handleReduceHalf = async (position, e) => {
        if (e) e.stopPropagation();
        setPositionToReduce(position);
        onReducePopoverOpen();
    };

    const onCloseConfirm = async () => {
        if (!positionToClose) return;
        
        try {
            setIsLoading(true);
            const response = await closePosition(positionToClose.id);
            
            if (response.success) {
                toast({
                    title: "清仓成功",
                    description: response.message,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                await fetchPositions();
            } else {
                toast({
                    title: "清仓失败",
                    description: response.error,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "清仓失败",
                description: error.response?.data?.error || error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
            setPositionToClose(null);
            onClosePopoverClose();
        }
    };

    const onReduceConfirm = async () => {
        if (!positionToReduce) return;
        
        try {
            setIsLoading(true);
            const response = await reducePositionHalf(positionToReduce.id);
            
            if (response.success) {
                toast({
                    title: "减仓成功",
                    description: response.message,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                await fetchPositions();
            } else {
                toast({
                    title: "减仓失败",
                    description: response.error,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "减仓失败",
                description: error.response?.data?.error || error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
            setPositionToReduce(null);
            onReducePopoverClose();
        }
    };

    const handleUpdateStockStopLossPrice = async (position, newStopLossPrice) => {
        try {
            setIsLoading(true);
            const response = await updateStockStopLossPrice(position.id, newStopLossPrice);
            
            if (response.success) {
                toast({
                    title: "止损价更新成功",
                    description: response.message,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                await fetchPositions();
            } else {
                toast({
                    title: "止损价更新失败",
                    description: response.error,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "止损价更新失败",
                description: error.response?.data?.error || error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box p={4}>
            <Flex mb={4} gap={4} align="center">
                <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">显示所有日期</FormLabel>
                    <Switch
                        isChecked={filters.showAllDates}
                        onChange={(e) => setFilters({ ...filters, showAllDates: e.target.checked })}
                    />
                </FormControl>
                <Input
                    placeholder="搜索标的"
                    value={filters.symbol}
                    onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
                    maxW="200px"
                />
                <Select
                    placeholder="选择状态"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    maxW="200px"
                >
                    <option value="OPEN">开仓</option>
                    <option value="PARTIAL_CLOSED">部分平仓</option>
                    <option value="CLOSED">平仓</option>
                </Select>
                <Select
                    placeholder="选择策略"
                    value={filters.strategy}
                    onChange={(e) => setFilters({ ...filters, strategy: e.target.value })}
                    maxW="200px"
                >
                    <option value="BREAKOUT">突破策略</option>
                    <option value="DOOMSDAY">末日轮策略</option>
                    <option value="CUSTOM">自定义策略</option>
                    <option value="GAP_FILL">缺口回补策略</option>
                    <option value="GAP">缺口策略</option>
                </Select>
                <Button
                    leftIcon={<RepeatIcon />}
                    onClick={handleRefresh}
                    isLoading={isLoading}
                    colorScheme="blue"
                    size="md"
                >
                    刷新
                </Button>
                <Text fontWeight="bold" ml="auto">
                    总盈亏：
                    <Text as="span" color={calculateTotalPnL() >= 0 ? "green.500" : "red.500"}>
                        ${calculateTotalPnL().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                </Text>
            </Flex>

            <Box overflowX="auto">
                <Table variant="striped" size="sm" colorScheme="gray">
                    <Thead>
                        <Tr>
                            <Th>标的</Th>
                            <Th>期权代码</Th>
                            <Th isNumeric>数量</Th>
                            <Th isNumeric>入场价</Th>
                            <Th isNumeric>现价</Th>
                            <Th isNumeric>止损价</Th>
                            <Th isNumeric>初始止损</Th>
                            <Th isNumeric>股票买入价</Th>
                            <Th isNumeric>股票止损价</Th>
                            <Th>类型</Th>
                            <Th whiteSpace="nowrap">入场时间</Th>
                            <Th whiteSpace="nowrap">更新时间</Th>
                            <Th>状态</Th>
                            <Th isNumeric>剩余</Th>
                            <Th isNumeric>盈亏</Th>
                            <Th>策略</Th>
                            <Th minW="150px">信号原因</Th>
                            <Th>操作</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {positions.map((position) => (
                            <Tr 
                                key={position.id}
                                _hover={{ bg: "blue.100" }}
                                cursor="pointer"
                                onClick={() => handleRowClick(position)}
                            >
                                <Td py={2}>{position.symbol}</Td>
                                <Td py={2}>{position.option_symbol}</Td>
                                <Td py={2} isNumeric>{position.quantity}</Td>
                                <Td py={2} isNumeric>{position.entry_price}</Td>
                                <Td py={2} isNumeric>{position.current_price}</Td>
                                <Td py={2} isNumeric>{position.stop_loss_price}</Td>
                                <Td py={2} isNumeric>{position.initial_stop_loss}</Td>
                                <Td py={2} isNumeric>{position.stock_entry_price || '-'}</Td>
                                <Td py={2} isNumeric>{position.stock_stop_loss_price || '-'}</Td>
                                <Td py={2}>{getPositionTypeBadge(position.position_type)}</Td>
                                <Td py={2} whiteSpace="nowrap">{formatDateTime(position.entry_time)}</Td>
                                <Td py={2} whiteSpace="nowrap">{formatDateTime(position.last_updated)}</Td>
                                <Td py={2}>{getStatusBadge(position.status)}</Td>
                                <Td py={2} isNumeric>{position.remaining_quantity}</Td>
                                <Td py={2} isNumeric>
                                    <Text color={position.total_profit_loss >= 0 ? "green.500" : "red.500"}>
                                        {position.total_profit_loss ? `$${position.total_profit_loss}` : '-'}
                                    </Text>
                                </Td>
                                <Td py={2}>{position.strategy}</Td>
                                <Td py={2}>{position.signal_reason}</Td>
                                <Td py={2}>
                                    {(position.status === 'OPEN' || position.status === 'PARTIAL_CLOSED') && (
                                        <Flex gap={2}>
                                            <Popover
                                                isOpen={isClosePopoverOpen}
                                                onClose={onClosePopoverClose}
                                                placement="right"
                                                closeOnBlur={false}
                                            >
                                                <PopoverTrigger>
                                                    <Button
                                                        leftIcon={<DeleteIcon />}
                                                        onClick={(e) => handleClosePosition(position, e)}
                                                        colorScheme="red"
                                                        size="sm"
                                                    >
                                                        清仓
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent width="200px">
                                                    <PopoverArrow />
                                                    <PopoverCloseButton />
                                                    <PopoverHeader fontWeight="bold">确认清仓</PopoverHeader>
                                                    <PopoverBody>
                                                        确定要清仓 {position.option_symbol} 吗？
                                                    </PopoverBody>
                                                    <PopoverFooter display="flex" justifyContent="flex-end">
                                                        <Button 
                                                            size="sm" 
                                                            variant="ghost" 
                                                            mr={3} 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onClosePopoverClose();
                                                            }}
                                                        >
                                                            取消
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            colorScheme="red" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onCloseConfirm();
                                                            }}
                                                            isLoading={isLoading}
                                                        >
                                                            确认
                                                        </Button>
                                                    </PopoverFooter>
                                                </PopoverContent>
                                            </Popover>

                                            <Popover
                                                isOpen={isReducePopoverOpen}
                                                onClose={onReducePopoverClose}
                                                placement="right"
                                                closeOnBlur={false}
                                            >
                                                <PopoverTrigger>
                                                    <Button
                                                        leftIcon={<DeleteIcon />}
                                                        onClick={(e) => handleReduceHalf(position, e)}
                                                        colorScheme="orange"
                                                        size="sm"
                                                    >
                                                        减仓一半
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent width="200px">
                                                    <PopoverArrow />
                                                    <PopoverCloseButton />
                                                    <PopoverHeader fontWeight="bold">确认减仓</PopoverHeader>
                                                    <PopoverBody>
                                                        确定要减仓一半 {position.option_symbol} 吗？
                                                    </PopoverBody>
                                                    <PopoverFooter display="flex" justifyContent="flex-end">
                                                        <Button 
                                                            size="sm" 
                                                            variant="ghost" 
                                                            mr={3} 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onReducePopoverClose();
                                                            }}
                                                        >
                                                            取消
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            colorScheme="orange" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onReduceConfirm();
                                                            }}
                                                            isLoading={isLoading}
                                                        >
                                                            确认
                                                        </Button>
                                                    </PopoverFooter>
                                                </PopoverContent>
                                            </Popover>

                                            <Button
                                                leftIcon={<EditIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingPosition(position);
                                                    setStopLossPrice(position.stock_stop_loss_price || "");
                                                    onStopLossOpen();
                                                }}
                                                colorScheme="blue"
                                                size="sm"
                                            >
                                                修改止损
                                            </Button>
                                        </Flex>
                                    )}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} size="6xl">
                <ModalOverlay />
                <ModalContent maxW="90vw">
                    <ModalHeader>
                        <Flex direction="column">
                            <Text>交易历史 - {selectedPosition?.symbol}</Text>
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                期权代码: {selectedPosition?.option_symbol}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                策略: {selectedPosition?.strategy}
                            </Text>
                            {selectedPosition?.signal_reason && (
                                <Text fontSize="sm" color="gray.500">
                                    信号原因: {selectedPosition?.signal_reason}
                                </Text>
                            )}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>时间</Th>
                                        <Th>类型</Th>
                                        <Th>数量</Th>
                                        <Th>价格</Th>
                                        <Th>盈亏</Th>
                                        <Th minW="200px">原因</Th>
                                        <Th minW="300px">详细说明</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {tradeHistory.map((trade) => (
                                        <Tr key={trade.id}>
                                            <Td whiteSpace="nowrap">{formatDateTime(trade.trade_time)}</Td>
                                            <Td>{getTradeTypeBadge(trade.trade_type)}</Td>
                                            <Td>{trade.quantity}</Td>
                                            <Td>
                                                {trade.trade_type === 'ENTRY' 
                                                    ? trade.entry_price 
                                                    : trade.exit_price}
                                            </Td>
                                            <Td color={trade.profit_loss >= 0 ? 'green.500' : 'red.500'}>
                                                {trade.profit_loss ? `${trade.profit_loss}` : '-'}
                                            </Td>
                                            <Td>{trade.reason}</Td>
                                            <Td>{trade.reason_detail || '-'}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* 修改股票止损价弹窗 */}
            <Modal isOpen={isStopLossOpen} onClose={onStopLossClose} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>修改股票止损价</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>标的: {editingPosition?.symbol}</FormLabel>
                            <FormLabel>当前股票止损价: {editingPosition?.stock_stop_loss_price || "未设置"}</FormLabel>
                            <FormLabel mt={4}>新的股票止损价:</FormLabel>
                            <Input
                                type="number"
                                step="0.01"
                                value={stopLossPrice}
                                onChange={(e) => setStopLossPrice(e.target.value)}
                                placeholder="输入新的止损价"
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            colorScheme="blue" 
                            mr={3} 
                            isLoading={isLoading}
                            onClick={() => {
                                handleUpdateStockStopLossPrice(editingPosition, stopLossPrice);
                                onStopLossClose();
                            }}
                        >
                            保存
                        </Button>
                        <Button variant="ghost" onClick={onStopLossClose}>取消</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default OptionPositions; 